export const PAGE_STRUCTURE = {
  home: {
    id: "home",
    label: "Home",
    navLabel: "Home",
    signedInNavLabel: "Dashboard",
    mapLabel: "Home",
    icon: "\u25CE",
    defaultSubpage: null,
    signedInDefaultSubpage: null,
    subpages: [],
    shell: {
      visitor: {
        eyebrow: "GLOBAL CHESS ARENA",
        title: "Home",
        description: "Build a team, enter leagues, and run official matches from one competition platform.",
      },
      signedIn: {
        eyebrow: "COMMAND CENTER",
        title: "Command Center",
        description: "Manage your team and track your competitive journey from one command center.",
      },
    },
  },
  account: {
    id: "account",
    label: "Profile",
    navLabel: "Sign In",
    signedInNavLabel: "Profile",
    mapLabel: "Profile",
    icon: "\u2699",
    signedInIconOnly: false,
    defaultSubpage: "profile",
    signedInDefaultSubpage: "profile",
    subpages: [{ id: "profile", label: "Profile" }],
    shell: {
      visitor: {
        eyebrow: "PLAYER PROFILE",
        title: "Profile",
        description: "Create an account, link your playing identities, and step into the arena with a clean competitive profile.",
      },
      signedIn: {
        eyebrow: "PLAYER PROFILE",
        title: "Profile",
        description: "Manage profile details, linked platforms, and launch access from one place before match night starts.",
      },
    },
  },
  teams: {
    id: "teams",
    label: "Teams",
    navLabel: "Teams",
    signedInNavLabel: "My Team",
    mapLabel: "Teams",
    icon: "\u265F",
    defaultSubpage: "overview",
    signedInDefaultSubpage: "my-team",
    subpages: [
      { id: "overview", label: "Overview" },
      { id: "my-team", label: "My Team", requiresSignIn: true },
      { id: "discover", label: "Discover" },
      { id: "players", label: "Players" },
      { id: "invites", label: "Invites", requiresSignIn: true },
      { id: "table", label: "Top 10" },
      { id: "team-page", label: "Team Profile" },
    ],
    shell: {
      visitor: {
        eyebrow: "TEAM DIRECTORY",
        title: "Teams",
        description: "Browse clubs, compare platform coverage, and follow roster strength before you sit down at a board.",
      },
      signedIn: {
        eyebrow: "TEAM COMMAND",
        title: "My Team",
        description: "Manage your roster, scout the field, and keep one clear path from invites to live boards.",
      },
    },
  },
  leagues: {
    id: "leagues",
    label: "Leagues",
    navLabel: "Leagues",
    signedInNavLabel: "Leagues",
    mapLabel: "Leagues",
    icon: "\u265A",
    defaultSubpage: "overview",
    signedInDefaultSubpage: "overview",
    subpages: [
      { id: "overview", label: "Overview" },
      { id: "edit", label: "Edit", requiresSignIn: true },
      { id: "rules", label: "Rules" },
    ],
    shell: {
      visitor: {
        eyebrow: "COMPETITION HUB",
        title: "Leagues",
        description: "Open the competition room, read the published rules, and follow the live bracket without stepping into admin controls.",
      },
      signedIn: {
        eyebrow: "COMPETITION HUB",
        title: "Leagues",
        description: "Create competitions, register teams instantly, seed the field, and move rounds forward without burying controls in one long page.",
      },
    },
  },
  play: {
    id: "play",
    label: "Matches",
    navLabel: "Matches",
    signedInNavLabel: "Matches",
    mapLabel: "Matches",
    icon: "\u2656",
    defaultSubpage: "matches",
    signedInDefaultSubpage: "matches",
    subpages: [
      { id: "matches", label: "Matches" },
      { id: "create", label: "Create" },
    ],
    shell: {
      visitor: {
        eyebrow: "MATCH CENTER",
        title: "Matches",
        description: "Pair the round, launch the right site, and keep every board result tied back to the same official match record.",
      },
      signedIn: {
        eyebrow: "MATCH CENTER",
        title: "Matches",
        description: "Control requests, board readiness, launch flow, and score tracking from one connected operating desk.",
      },
    },
  },
  forums: {
    id: "forums",
    label: "Round Room",
    navLabel: "Round Room",
    signedInNavLabel: "Round Room",
    mapLabel: "Round Room",
    icon: "\u270E",
    defaultSubpage: null,
    signedInDefaultSubpage: null,
    subpages: [],
    shell: {
      visitor: {
        eyebrow: "ROUND CHAT",
        title: "Round Room",
        description: "Keep announcements, substitutions, and scheduling notes anchored to the week instead of scattered across private chats.",
      },
      signedIn: {
        eyebrow: "ROUND CHAT",
        title: "Round Room",
        description: "Use one structured room for captain notes, substitutions, scheduling threads, and weekly league announcements.",
      },
    },
  },
  disputes: {
    id: "disputes",
    label: "Review Desk",
    navLabel: "Review Desk",
    signedInNavLabel: "Review Desk",
    mapLabel: "Review Desk",
    icon: "\u2696",
    defaultSubpage: null,
    signedInDefaultSubpage: null,
    subpages: [],
    shell: {
      visitor: {
        eyebrow: "ARBITER FLOW",
        title: "Review Desk",
        description: "Bring the board record, evidence, and ruling trail together so corrections are made against facts, not memory.",
      },
      signedIn: {
        eyebrow: "ARBITER FLOW",
        title: "Review Desk",
        description: "Use one review path for disputes, evidence, and official score corrections tied back to the match record.",
      },
    },
  },
  contact: {
    id: "contact",
    label: "Support Desk",
    navLabel: "Support",
    signedInNavLabel: "Support",
    mapLabel: "Support Desk",
    icon: "\u260E",
    defaultSubpage: null,
    signedInDefaultSubpage: null,
    subpages: [],
    shell: {
      visitor: {
        eyebrow: "SUPPORT ROUTING",
        title: "Support Desk",
        description: "Route account issues, league operations, and arbiter requests to the right desk the first time.",
      },
      signedIn: {
        eyebrow: "SUPPORT ROUTING",
        title: "Support Desk",
        description: "Move account, league, and arbiter questions into the correct lane so the workspace stays calm during active rounds.",
      },
    },
  },
};

function withNavMeta(page, { signedIn } = {}) {
  return {
    ...page,
    navText: signedIn ? page.signedInNavLabel || page.navLabel || page.label : page.navLabel || page.label,
    isIconOnly: Boolean(signedIn && page.signedInIconOnly),
  };
}

export function getPageDefinition(pageId) {
  return PAGE_STRUCTURE[pageId] || PAGE_STRUCTURE.home;
}

export function getPrimaryNavItems({ signedIn } = {}) {
  return ["home", "leagues", "play", "teams"].map((pageId) =>
    withNavMeta(getPageDefinition(pageId), { signedIn })
  );
}

export function getSecondaryNavItem({ signedIn } = {}) {
  return withNavMeta(getPageDefinition("account"), { signedIn });
}

export function getPageMapItems({ signedIn } = {}) {
  return ["home", "account", "teams", "leagues", "play", "forums", "disputes", "contact"].map((pageId) =>
    withNavMeta(getPageDefinition(pageId), { signedIn })
  );
}

export function getPageShell(pageId, { signedIn, subpage } = {}) {
  const page = getPageDefinition(pageId);
  const shell = signedIn ? page.shell?.signedIn || page.shell?.visitor : page.shell?.visitor || page.shell?.signedIn;
  const activeSubpage = (page.subpages || []).find((entry) => entry.id === subpage);

  if (!shell) return null;

  return {
    ...shell,
    activeViewLabel: activeSubpage?.label || "",
  };
}
